'use client';

import { useCopyToClipboard } from '@uidotdev/usehooks';
import type { ReactNode } from 'react';
import React, { useEffect, useState } from 'react';

import { cn } from '@skrym/shared/utils/cn';

interface CodeProps {
  children: ReactNode;
}

// This component handles inline code
function Code(props: CodeProps) {
  const { children } = props;

  const [_, copyToClipboard] = useCopyToClipboard();
  const [isCopied, setIsCopied] = useState(false);

  // Function to handle click event and copy text to clipboard
  const handleClick = async () => {
    let textToCopy = '';

    React.Children.forEach(children, (child) => {
      if (typeof child === 'string') {
        textToCopy += child;
      }
    });

    if (textToCopy) {
      await copyToClipboard(textToCopy);
      setIsCopied(true);
    }
  };

  useEffect(() => {
    if (isCopied) {
      // Reset isCopied after 2 seconds
      const timer = setTimeout(() => {
        setIsCopied(false);
      }, 200);

      return () => clearTimeout(timer);
    }
  }, [isCopied]);

  return (
    <code
      onClick={handleClick}
      onKeyDown={handleClick}
      title={isCopied ? 'Copied!' : 'Click to copy'}
      className={cn(
        'relative rounded-sm bg-muted/50 hover:bg-muted border border-border px-[0.25rem] py-[0.1rem] text-[smaller] font-mono font-medium cursor-pointer',
      )}
    >
      {children}
    </code>
  );
}

export default Code;
